import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useParams } from 'react-router-dom';
import cardService from '../services/cardService';
import insertSetService from '../services/insertSetService';
import slugService from '../services/slugService';
import CardItem from '../components/CardItem';
import PageGrid from '../components/PageGrid';
import Sidebar from '../components/Sidebar';
import SidebarSection from '../components/Sidebar/SidebarSection';
import SidebarRow from '../components/Sidebar/SidebarRow';
import CardActions from '../components/CardActions';
import WishlistInsertSetIcon from '../components/WishlistInsertSetIcon';
import TradeInsertSetIcon from '../components/TradeInsertSetIcon';
import CollectionInsertSetIcon from '../components/CollectionInsertSetIcon';
import { AuthContext } from '../context/AuthContext';
import usePageTitle from '../utils/usePageTitle';
import { getInsertSetBreadcrumbs } from '../utils/breadcrumbUtils';
import { filterCards } from '../utils/filters';
import './InsertSetPage.css';
import Legend from '../components/Legend';

const InsertSetPage = () => {
  const { productName, insertSetSlug } = useParams();
  const [insertSet, setInsertSet] = useState(null);
  const [product, setProduct] = useState(null);
  const { currentUser } = useContext(AuthContext);

  // Fetch insert set data on initial load
  useEffect(() => {
    const fetchInsertSetData = async () => {
      if (insertSetSlug) {
        try {
          // Extract ID from slug
          const parsedSlug = slugService.parseSlugWithId(insertSetSlug);
          if (!parsedSlug.id) {
            throw new Error('Could not extract ID from insert set slug');
          }
          
          // Fetch insert set data
          const insertSetData = await insertSetService.getInsertSetById(parsedSlug.id);
          setInsertSet(insertSetData);
          
          // Fetch product data if we have a product ID
          if (insertSetData.productId) {
            const productData = await insertSetService.getProductForInsertSet(insertSetData);
            setProduct(productData);
          }
        } catch (error) {
          console.error('Error fetching insert set data:', error);
        }
      }
    };
    
    fetchInsertSetData();
  }, [productName, insertSetSlug]);
  
  // Function to fetch cards for a specific page
  const fetchCards = useCallback(async (page, limit) => {
    if (!insertSet) {
      return {
        items: [],
        currentPage: 1,
        totalPages: 1,
        totalItems: 0
      };
    }
    
    try {
      const response = await cardService.getCardsByInsertSetByPage(insertSet, page, limit);
      
      return {
        items: response.cards,
        currentPage: response.currentPage,
        totalPages: response.totalPages,
        totalItems: response.totalCards
      };
    } catch (error) {
      console.error('Error fetching cards:', error);
      throw error;
    }
  }, [insertSet]);
  
  
  // Function to render each card
  const renderCard = useCallback((card, index) => {
    const cardUrl = slugService.getCardUrl(card, insertSet, product);
    
    return (
      <CardItem 
        card={card}
        showImage={true}
        index={index}
        inventory={null} // Could be populated from user data
        inWishlist={false} // Could be populated from user data
        insertSet={insertSet}
        product={product}
        cardUrl={cardUrl}
      />
    );
  }, [insertSet, product]);
  
  // Prepare the title including insert set and product info
  const title = insertSet && product
    ? `${insertSet.name} - ${product.name} (${product.year})`
    : '';
    
  // Set the page title using array format
  usePageTitle(insertSet && product 
    ? [insertSet.name, `${product.name} (${product.year})`]
    : 'Insert Set');
  
  
  // Render the sidebar content with product and insert set details
  const renderSidebar = useCallback(() => {
    if (!product || !insertSet) return null;
    
    return (
      <Sidebar title="Set Information">
        <SidebarSection title="Product Details">
          <SidebarRow
            label="Product"
            value={product.name}
          />
          <SidebarRow
            label="Year"
            value={product.year}
          />
          {product.manufacturer && (
            <SidebarRow
              label="Manufacturer"
              value={product.manufacturer}
            />
          )}
        </SidebarSection>
        
        <SidebarSection title="Insert Set Details">
          <SidebarRow
            label="Set Name"
            value={insertSet.name}
          />
          {insertSet.description && (
            <SidebarRow
              label="Description"
              value={insertSet.description}
            />
          )}
          {insertSet.odds && (
            <SidebarRow
              label="Pack Odds"
              value={insertSet.odds}
            />
          )}
          {insertSet.totalCards && (
            <SidebarRow
              label="Total Cards"
              value={insertSet.totalCards}
            />
          )}
        </SidebarSection>
      </Sidebar>
    );
  }, [product, insertSet]);
  
  if(!insertSet || !product) {
    return <></>;
  }
  
  // Generate breadcrumb items for this insert set page
  const breadcrumbItems = getInsertSetBreadcrumbs(insertSet, product);
  
  // Create top action menu with inventory, trade and wishlist icons if user is logged in
  const topActionMenu = currentUser && insertSet ? (
    <CardActions
      leftActions={<></>}
      rightActions={
        <>
          <CollectionInsertSetIcon size={40} insertSet={insertSet} />
          <TradeInsertSetIcon size={40} insertSet={insertSet} />
          <WishlistInsertSetIcon size={40} insertSet={insertSet} />
        </>
      }
    />
  ) : null;

  return (
    <PageGrid
      title={title}
      fetchItems={fetchCards}
      renderItem={renderCard}
      renderSidebar={renderSidebar}
      filterFunction={filterCards}
      placeholderText="Filter cards by name or number..."
      emptyStateMessage="No cards found for this insert set"
      itemsPerPage={100}
      breadcrumbItems={breadcrumbItems}
      topActionMenu={topActionMenu}
      footer={<Legend showBulk={true} />}
    />
  );
};

export default InsertSetPage;